// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module PlausibleProvider
 * @package @geenius/analytics
 * @description Plausible analytics provider. Posts events directly to
 * Plausible's `/api/event` endpoint (https://plausible.io/docs/events-api).
 * No external client library — the deprecated `plausible-tracker` npm package
 * is no longer required.
 */

import type { AnalyticsEvent, PageView } from "../types";
import {
  type AnalyticsProviderConfig,
  BaseAnalyticsProvider,
} from "./interface";

const DEFAULT_API_HOST = "https://plausible.io";

interface PlausibleEventBody {
  name: string;
  url: string;
  domain: string;
  referrer?: string;
  props?: Record<string, unknown>;
}

/**
 * Plausible analytics provider backed by the native Plausible Events API.
 * Posts events directly via fetch — no third-party wrapper required.
 */
export class PlausibleProvider extends BaseAnalyticsProvider {
  readonly name = "plausible" as const;

  /**
   * Stores the Plausible configuration for event dispatch.
   *
   * @param config Provider configuration. `apiKey` is the Plausible domain
   *   (e.g. `"geenius.dev"`). `host` overrides the default API host for
   *   self-hosted Plausible instances.
   */
  init(config: AnalyticsProviderConfig): void {
    super.init(config);
  }

  private async send(body: PlausibleEventBody): Promise<void> {
    const config = this.getRequiredConfig();
    // Mirror the old `trackLocalhost: config.debug ?? false` default —
    // skip the network call when not in debug mode and running on localhost.
    if (!config.debug && this.isLocalhost()) {
      return;
    }
    const host = (config.host ?? DEFAULT_API_HOST).replace(/\/+$/, "");
    try {
      await fetch(`${host}/api/event`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        // keepalive: true ensures the request completes even when fired
        // during page-unload — matching the old tracker's browser behaviour.
        keepalive: true,
      });
    } catch {
      // Plausible explicitly accepts silent failures on the client.
      // The prior tracker swallowed network errors identically.
    }
  }

  private isLocalhost(): boolean {
    if (typeof globalThis === "undefined" || !("location" in globalThis))
      return false;
    const host = (globalThis as typeof globalThis & { location?: Location })
      .location?.hostname;
    return host === "localhost" || host === "127.0.0.1";
  }

  /**
   * Sends a shared analytics event to Plausible via the Events API.
   *
   * @param event Shared analytics event payload.
   */
  async trackEvent(event: AnalyticsEvent): Promise<void> {
    const config = this.getRequiredConfig();
    await this.send({
      name: event.name,
      url: event.url,
      domain: config.apiKey,
      referrer: event.referrer,
      props: {
        ...event.properties,
        event_type: event.type,
      },
    });
  }

  /**
   * Sends a page-view payload to Plausible via the Events API.
   *
   * @param pageView Shared page-view payload.
   */
  async trackPageView(pageView: PageView): Promise<void> {
    const config = this.getRequiredConfig();
    await this.send({
      name: "pageview",
      url: pageView.url,
      domain: config.apiKey,
      referrer: pageView.referrer,
      ...(pageView.properties ? { props: pageView.properties } : {}),
    });
  }
}
